/**
 * ReachabilityLabPanel — the DATA.REACH reachability slice (interactive).
 *
 * Presents a finite authority DAG; the user picks a source and a
 * destination; the panel runs the reachability slice decision procedure
 * (algorithms/reachability.ts) and renders the slice as an SVG node-link
 * diagram, highlighting slice nodes and edges and reporting the forward
 * set, backward set, and minimal slice (RD-27 §6, ABK-2 / ABK-3).
 *
 * SPDX-License-Identifier: LicenseRef-Proprietary
 * Copyright (c) 2026 Invariant Holdings LLC
 */
import React, { useMemo, useState } from "react";
import { ArrowheadMarker, PanelSurface, panelTokens } from "@csk/demo-ui";
import dagsData from "../data/dags.json" with { type: "json" };
import {
  edgeInSlice,
  reachabilitySlice,
  type AuthorityDag,
} from "../algorithms/reachability.js";
type Preset = (typeof dagsData.presets)[number];
function presetById(id: string): Preset {
  return dagsData.presets.find((p) => p.id === id) ?? dagsData.presets[0];
}
const NODE_RADIUS = 26;
const SVG_WIDTH = 540;
const SVG_HEIGHT = 380;
export function ReachabilityLabPanel(): React.JSX.Element {
  const [presetId, setPresetId] = useState<string>(dagsData.presets[0].id);
  const preset = useMemo(() => presetById(presetId), [presetId]);
  const [source, setSource] = useState<string>(preset.defaultSource);
  const [destination, setDestination] = useState<string>(
    preset.defaultDestination,
  );
  React.useEffect(() => {
    setSource(preset.defaultSource);
    setDestination(preset.defaultDestination);
  }, [preset]);
  const dag: AuthorityDag = preset;
  const slice = useMemo(
    () => reachabilitySlice(dag, source, destination),
    [dag, source, destination],
  );
  const sliceNodeSet = useMemo(
    () => new Set(slice.sliceNodes),
    [slice.sliceNodes],
  );
  const nodePosition = useMemo(() => {
    const map = new Map<
      string,
      {
        x: number;
        y: number;
      }
    >();
    for (const n of preset.nodes) map.set(n.id, { x: n.x, y: n.y });
    return map;
  }, [preset]);
  const labelFor = (id: string): string =>
    preset.nodes.find((n) => n.id === id)?.label ?? id;
  const tone: "safe" | "unsafe" = slice.reachable ? "safe" : "unsafe";
  const verdictTitle = slice.reachable
    ? `${labelFor(destination)} is REACHABLE from ${labelFor(source)}`
    : `${labelFor(destination)} is NOT reachable from ${labelFor(source)}`;
  const verdictBody = slice.reachable
    ? `A DATA.REACH morphism exists. The minimal slice has ${String(slice.sliceNodes.length)} node${slice.sliceNodes.length === 1 ? "" : "s"} and ${String(slice.sliceEdges.length)} edge${slice.sliceEdges.length === 1 ? "" : "s"} (ABK-2 slice-sound witnesses the path).`
    : `FWRD(s) ∩ BACK(d) is empty: no authority path connects the source to the destination, so no Declassify witness can be exhibited (ABK-3 slice-complete).`;
  return (
    <PanelSurface.PanelShell
      title="Reachability Slice Lab"
      subtitle="The DATA.REACH decision procedure on a finite authority DAG: FWRD(s) ∩ BACK(d) is the minimal slice."
      accentColor={panelTokens.accentCyan}
    >
      <PanelSurface.Card>
        <p className="m-0 text-[14px] leading-[1.7] text-[var(--abk-text)]">
          The DATA.REACH query asks: can an agent transmit information to a
          destination under the current delegation graph? The decision procedure
          computes the forward reachable set from the source, the backward
          reachable set into the destination, and intersects them. The
          intersection is the minimal reachability slice; the destination is
          reachable iff it lies in the slice. Runs in O(V + E).
        </p>
      </PanelSurface.Card>

      <PanelSurface.SectionHeading>Authority DAG</PanelSurface.SectionHeading>
      <PanelSurface.Card>
        <PanelSurface.ChipRow
          ariaLabel="Authority DAG preset"
          activeId={presetId}
          onChange={setPresetId}
          items={dagsData.presets.map((p) => ({
            id: p.id,
            label: p.title,
            color: panelTokens.accentCyan,
          }))}
        />
        <p className="mx-1 mt-3 mb-0 text-[12.5px] leading-[1.6] text-[var(--abk-muted)]">
          {preset.summary}
        </p>
      </PanelSurface.Card>

      <PanelSurface.SectionHeading>
        Source and destination
      </PanelSurface.SectionHeading>
      <PanelSurface.Card>
        <PanelSurface.SectionLabel>Source (s)</PanelSurface.SectionLabel>
        <PanelSurface.ChipRow
          ariaLabel="Source node"
          activeId={source}
          onChange={setSource}
          items={preset.nodes.map((n) => ({
            id: n.id,
            label: n.label,
            color: panelTokens.accentGreen,
          }))}
        />
        <PanelSurface.SectionLabel className="mt-3">
          Destination (d)
        </PanelSurface.SectionLabel>
        <PanelSurface.ChipRow
          ariaLabel="Destination node"
          activeId={destination}
          onChange={setDestination}
          items={preset.nodes.map((n) => ({
            id: n.id,
            label: n.label,
            color: panelTokens.accentForbidden,
          }))}
        />
      </PanelSurface.Card>

      <PanelSurface.SectionHeading>Slice diagram</PanelSurface.SectionHeading>
      <PanelSurface.Card>
        <svg
          role="img"
          aria-label={`Authority DAG with reachability slice from ${labelFor(source)} to ${labelFor(destination)}`}
          viewBox={`0 0 ${String(SVG_WIDTH)} ${String(SVG_HEIGHT)}`}
          className="max-h-[380px] w-full"
        >
          <defs>
            <ArrowheadMarker id="abk-arrow" color={panelTokens.muted} />
            <ArrowheadMarker
              id="abk-arrow-slice"
              color={panelTokens.accentCyan}
            />
          </defs>

          {preset.edges.map((e) => {
            const a = nodePosition.get(e.from);
            const b = nodePosition.get(e.to);
            if (a === undefined || b === undefined) return null;
            const inSlice = edgeInSlice(slice, e);
            const dx = b.x - a.x;
            const dy = b.y - a.y;
            const len = Math.hypot(dx, dy) || 1;
            const ux = dx / len;
            const uy = dy / len;
            const x1 = a.x + ux * NODE_RADIUS;
            const y1 = a.y + uy * NODE_RADIUS;
            const x2 = b.x - ux * (NODE_RADIUS + 4);
            const y2 = b.y - uy * (NODE_RADIUS + 4);
            return (
              <line
                key={`${e.from}->${e.to}`}
                x1={x1}
                y1={y1}
                x2={x2}
                y2={y2}
                stroke={inSlice ? panelTokens.accentCyan : panelTokens.border}
                strokeWidth={inSlice ? 2.5 : 1.5}
                markerEnd={
                  inSlice ? "url(#abk-arrow-slice)" : "url(#abk-arrow)"
                }
                opacity={inSlice ? 1 : 0.6}
              />
            );
          })}

          {preset.nodes.map((n) => {
            const inSlice = sliceNodeSet.has(n.id);
            const isSource = n.id === source;
            const isDest = n.id === destination;
            const fill = isSource
              ? panelTokens.accentGreen
              : isDest
                ? panelTokens.accentForbidden
                : inSlice
                  ? panelTokens.accentCyan
                  : panelTokens.elev;
            const stroke =
              inSlice || isSource || isDest ? fill : panelTokens.border;
            return (
              <g key={n.id}>
                <circle
                  cx={n.x}
                  cy={n.y}
                  r={NODE_RADIUS}
                  fill={`${fill}22`}
                  stroke={stroke}
                  strokeWidth={inSlice || isSource || isDest ? 2.5 : 1.5}
                />
                <text
                  x={n.x}
                  y={n.y + 4}
                  textAnchor="middle"
                  fontSize="10"
                  fontWeight="600"
                  fill={panelTokens.bright}
                >
                  {n.label}
                </text>
              </g>
            );
          })}
        </svg>
        <PanelSurface.BadgeGroup
          className="mt-3"
          items={[
            { id: "source", label: "source", color: panelTokens.accentGreen },
            {
              id: "destination",
              label: "destination",
              color: panelTokens.accentForbidden,
            },
            {
              id: "slice",
              label: "slice node / edge",
              color: panelTokens.accentCyan,
            },
            {
              id: "ambient",
              label: "ambient (off-slice)",
              color: panelTokens.muted,
            },
          ]}
        />
        <div className="mt-3.5">
          <PanelSurface.VerdictBanner
            tone={tone}
            title={verdictTitle}
            body={verdictBody}
          />
        </div>
      </PanelSurface.Card>

      <PanelSurface.SectionHeading>
        Decision procedure trace
      </PanelSurface.SectionHeading>
      <PanelSurface.Card>
        <PanelSurface.Mono>{`FWRD(${source})  = { ${slice.forward.join(", ")} }
BACK(${destination})  = { ${slice.backward.join(", ")} }
slice  = FWRD(s) ∩ BACK(d) = { ${slice.sliceNodes.join(", ")} }
edges  = ${
          slice.sliceEdges.length === 0
            ? "{ }"
            : slice.sliceEdges.map((e) => `${e.from}→${e.to}`).join(", ")
        }
reachable(${source}, ${destination}) = ${String(slice.reachable)}   -- d ∈ slice`}</PanelSurface.Mono>
      </PanelSurface.Card>

      <PanelSurface.RepositoryLinks
        items={[
          {
            label: "RD-27 §6 Reachability Slice",
            path: "research/settled/RD-27-AGENTIC-BOUNDARY-KERNEL.md",
          },
          {
            label: "ReachabilitySlice.agda (ABK-2/ABK-3)",
            path: "agda/Research/AgenticBoundaryKernel/ReachabilitySlice.agda",
          },
          {
            label: "data-reach-reachability-slice.md",
            path: "research/agentic-boundary-kernel/data-reach-reachability-slice.md",
          },
        ]}
      />
    </PanelSurface.PanelShell>
  );
}
